import { useEffect, useState, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';
import {
  Pool,
  F1EntryPicks,
  F1RacePick,
  F1DriverPick,
  F1DriverUsage,
  F1_DRIVERS_PER_RACE,
  F1_REGULAR_MAX_USES,
  F1_PLAYOFF_MAX_USES,
  F1_REGULAR_SEASON_RACES,
} from '../types/pool';
import FeeNotice from '../components/FeeNotice';

// Official F1 Team Colors (2026 season) - based on official F1 graphics
// primary = solid background color, secondary = accent color, tertiary = text color (usually white)
const F1_TEAM_COLORS: Record<string, { primary: string; secondary: string; tertiary: string }> = {
  // McLaren - Papaya Orange
  'McLaren': { primary: '#FF8700', secondary: '#000000', tertiary: '#FFFFFF' },
  // Ferrari - Rosso Corsa Red
  'Ferrari': { primary: '#DC0000', secondary: '#FFD700', tertiary: '#FFFFFF' },
  'Scuderia Ferrari': { primary: '#DC0000', secondary: '#FFD700', tertiary: '#FFFFFF' },
  // Red Bull Racing - Dark Blue
  'Red Bull Racing': { primary: '#1E41FF', secondary: '#FFD700', tertiary: '#FFFFFF' },
  'Red Bull': { primary: '#1E41FF', secondary: '#FFD700', tertiary: '#FFFFFF' },
  'RBR': { primary: '#1E41FF', secondary: '#FFD700', tertiary: '#FFFFFF' },
  // Mercedes - Teal/Petronas Green
  'Mercedes': { primary: '#00D2BE', secondary: '#000000', tertiary: '#FFFFFF' },
  'Mercedes-AMG': { primary: '#00D2BE', secondary: '#000000', tertiary: '#FFFFFF' },
  'Mercedes-AMG Petronas': { primary: '#00D2BE', secondary: '#000000', tertiary: '#FFFFFF' },
  // Aston Martin - British Racing Green
  'Aston Martin': { primary: '#006F62', secondary: '#CEDC00', tertiary: '#FFFFFF' },
  // Alpine - Blue/Pink
  'Alpine': { primary: '#0090FF', secondary: '#FF4EC7', tertiary: '#FFFFFF' },
  // Williams - Blue
  'Williams': { primary: '#005AFF', secondary: '#FFFFFF', tertiary: '#FFFFFF' },
  // Racing Bulls (RB) - Dark Navy
  'RB': { primary: '#1E3A5F', secondary: '#E10600', tertiary: '#FFFFFF' },
  'VCARB': { primary: '#1E3A5F', secondary: '#E10600', tertiary: '#FFFFFF' },
  'Visa Cash App RB': { primary: '#1E3A5F', secondary: '#E10600', tertiary: '#FFFFFF' },
  'Visa Cash App Racing Bulls': { primary: '#1E3A5F', secondary: '#E10600', tertiary: '#FFFFFF' },
  'Racing Bulls': { primary: '#1E3A5F', secondary: '#E10600', tertiary: '#FFFFFF' },
  // Audi - Black with green accent
  'Audi': { primary: '#000000', secondary: '#00FF00', tertiary: '#FFFFFF' },
  'Audi F1 Team': { primary: '#000000', secondary: '#00FF00', tertiary: '#FFFFFF' },
  'Kick Sauber': { primary: '#00E701', secondary: '#000000', tertiary: '#FFFFFF' },
  'Sauber': { primary: '#00E701', secondary: '#000000', tertiary: '#FFFFFF' },
  // Haas - Red/White
  'Haas': { primary: '#E10600', secondary: '#FFFFFF', tertiary: '#FFFFFF' },
  'Haas F1 Team': { primary: '#E10600', secondary: '#FFFFFF', tertiary: '#FFFFFF' },
  // Cadillac - Black
  'Cadillac': { primary: '#000000', secondary: '#C4A747', tertiary: '#FFFFFF' },
  'Cadillac F1 Team': { primary: '#000000', secondary: '#C4A747', tertiary: '#FFFFFF' },
};

// Get team colors with fallback
function getTeamColors(teamName: string): { primary: string; secondary: string; tertiary: string; driverTextColor: string } {
  // Default fallback - F1 Red
  let colors = { primary: '#E10600', secondary: '#FFFFFF', tertiary: '#FFFFFF' };

  // Try exact match first
  if (F1_TEAM_COLORS[teamName]) {
    colors = F1_TEAM_COLORS[teamName];
  } else {
    // Try partial match
    const lowerName = teamName.toLowerCase();
    for (const [key, c] of Object.entries(F1_TEAM_COLORS)) {
      if (lowerName.includes(key.toLowerCase()) || key.toLowerCase().includes(lowerName)) {
        colors = c;
        break;
      }
    }
  }

  // Driver text is always white on these solid backgrounds
  return { ...colors, driverTextColor: '#FFFFFF' };
}

// Country flags for F1 race locations
const RACE_FLAGS: Record<string, string> = {
  // Common race location names
  'Bahrain': '🇧🇭',
  'Sakhir': '🇧🇭',
  'Saudi Arabia': '🇸🇦',
  'Jeddah': '🇸🇦',
  'Australia': '🇦🇺',
  'Melbourne': '🇦🇺',
  'Japan': '🇯🇵',
  'Suzuka': '🇯🇵',
  'China': '🇨🇳',
  'Shanghai': '🇨🇳',
  'Miami': '🇺🇸',
  'Monaco': '🇲🇨',
  'Monte Carlo': '🇲🇨',
  'Canada': '🇨🇦',
  'Montreal': '🇨🇦',
  'Spain': '🇪🇸',
  'Spanish': '🇪🇸',
  'Barcelona': '🇪🇸',
  'Catalunya': '🇪🇸',
  'Austria': '🇦🇹',
  'Spielberg': '🇦🇹',
  'Great Britain': '🇬🇧',
  'Silverstone': '🇬🇧',
  'United Kingdom': '🇬🇧',
  'Britain': '🇬🇧',
  'Hungary': '🇭🇺',
  'Budapest': '🇭🇺',
  'Belgium': '🇧🇪',
  'Belgian': '🇧🇪',
  'Spa-Francorchamps': '🇧🇪',
  'Netherlands': '🇳🇱',
  'Zandvoort': '🇳🇱',
  'Italy': '🇮🇹',
  'Monza': '🇮🇹',
  'Imola': '🇮🇹',
  'Azerbaijan': '🇦🇿',
  'Baku': '🇦🇿',
  'Singapore': '🇸🇬',
  'USA': '🇺🇸',
  'Austin': '🇺🇸',
  'Texas': '🇺🇸',
  'United States': '🇺🇸',
  'Mexico': '🇲🇽',
  'Mexico City': '🇲🇽',
  'Brazil': '🇧🇷',
  'São Paulo': '🇧🇷',
  'Sao Paulo': '🇧🇷',
  'Interlagos': '🇧🇷',
  'Las Vegas': '🇺🇸',
  'Qatar': '🇶🇦',
  'Qatari': '🇶🇦',
  'Lusail': '🇶🇦',
  'Abu Dhabi': '🇦🇪',
  'Yas Marina': '🇦🇪',
  'UAE': '🇦🇪',
  'Emilia Romagna': '🇮🇹',
  'Portugal': '🇵🇹',
  'Portimao': '🇵🇹',
};

// Get flag for race location - use word boundary matching to avoid "Spa" matching "Spanish"
function getRaceFlag(raceName: string, location: string | null): string {
  const searchText = `${raceName} ${location || ''}`.toLowerCase();

  // Sort keys by length (longest first) to match more specific terms first
  const sortedEntries = Object.entries(RACE_FLAGS).sort((a, b) => b[0].length - a[0].length);

  for (const [key, flag] of sortedEntries) {
    const keyLower = key.toLowerCase();
    // Use word boundary check - key should be a complete word or at word boundary
    const regex = new RegExp(`\\b${keyLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    if (regex.test(searchText)) {
      return flag;
    }
  }

  return '🏁'; // Default racing flag
}

// Transformed driver interface for internal use
interface F1Driver {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  position: string;
  teamId: string;
  teamName: string;
  teamShortName: string;
}

interface F1Race {
  id: string;
  name: string;
  raceNumber: number;
  scheduledAt: string;
  location: string | null;
  status: string;
  isSprintRace: boolean;
}

export function F1EntryForm() {
  const { poolId, entryId } = useParams<{ poolId: string; entryId?: string }>();
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();

  const [pool, setPool] = useState<Pool | null>(null);
  const [drivers, setDrivers] = useState<F1Driver[]>([]);
  const [races, setRaces] = useState<F1Race[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [entryName, setEntryName] = useState('');
  const [picks, setPicks] = useState<F1RacePick[]>([]);
  const [driverUsageHistory, setDriverUsageHistory] = useState<Record<string, F1DriverUsage>>({});
  const [phase, setPhase] = useState<'REGULAR' | 'PLAYOFF'>('REGULAR');
  const [isEditMode, setIsEditMode] = useState(false);

  // Collapse state for sections
  const [showDriverUsage, setShowDriverUsage] = useState(true);
  const [showRegularSeason, setShowRegularSeason] = useState(true);
  const [showPlayoffs, setShowPlayoffs] = useState(true);
  const [expandedRaces, setExpandedRaces] = useState<Set<string>>(new Set());

  // Load pool, drivers, and races
  useEffect(() => {
    if (!isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: window.location.pathname },
      });
      return;
    }

    async function loadData() {
      if (!poolId) return;

      try {
        setLoading(true);
        setError(null);

        // First fetch pool (supports both slug and ID)
        const poolData = await api.getPool(poolId);
        setPool(poolData);

        // Use the actual pool ID for subsequent API calls (in case poolId is a slug)
        const actualPoolId = poolData.id;

        // Fetch drivers from pool players and transform the data
        const playersResponse = await api.getPoolPlayers(actualPoolId);
        const rawPlayers = playersResponse.players || [];

        // Transform player data to F1Driver format
        const transformedDrivers: F1Driver[] = rawPlayers.map((p: any) => {
          // Parse name - could be "First Last" or just stored separately
          const nameParts = (p.name || '').split(' ');
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';

          return {
            id: p.id,
            name: p.name,
            firstName,
            lastName,
            position: p.position,
            teamId: p.teamId,
            teamName: p.team?.name || 'Unknown',
            teamShortName: p.team?.abbreviation || p.team?.shortName || 'UNK',
          };
        });

        setDrivers(transformedDrivers);

        // Fetch race schedule from pool events
        const eventsResponse = await api.getPoolEventsPublic(actualPoolId);
        const sortedRaces = (eventsResponse.events || [])
          .filter((e: any) => e.raceNumber)
          .sort((a: any, b: any) => a.raceNumber - b.raceNumber);
        setRaces(sortedRaces);

        // Auto-expand first few upcoming races
        const now = new Date();
        const upcomingRaceIds = sortedRaces
          .filter((r: any) => new Date(r.scheduledAt) > now)
          .slice(0, 3)
          .map((r: any) => r.id);
        setExpandedRaces(new Set(upcomingRaceIds));

        // If editing, load existing entry
        if (entryId && isAuthenticated) {
          setIsEditMode(true);
          const token = await getAccessTokenSilently();
          const existingEntry = await api.getMyEntry(actualPoolId, token);

          if (existingEntry && existingEntry.id === entryId) {
            setEntryName(existingEntry.entryName || '');
            const existingPicks = existingEntry.picks as F1EntryPicks;
            if (existingPicks?.type === 'F1_SEQUENTIAL_RACES') {
              setPicks(existingPicks.picks || []);
              setDriverUsageHistory(existingPicks.driverUsageHistory || {});
              const displayPhase = existingPicks.phase === 'ELIMINATED' ? 'PLAYOFF' : (existingPicks.phase || 'REGULAR');
              setPhase(displayPhase);
            }
          }
        }
      } catch (err) {
        console.error('Failed to load data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load pool data');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [poolId, entryId, isAuthenticated, getAccessTokenSilently]);

  // Split races into regular season and playoffs
  const { regularSeasonRaces, playoffRaces } = useMemo(() => {
    const regular = races.filter(r => r.raceNumber <= F1_REGULAR_SEASON_RACES);
    const playoffs = races.filter(r => r.raceNumber > F1_REGULAR_SEASON_RACES);
    return { regularSeasonRaces: regular, playoffRaces: playoffs };
  }, [races]);

  // Calculate driver usage across all races, grouped by team
  const driverUsageByTeam = useMemo(() => {
    const maxUses = phase === 'REGULAR' ? F1_REGULAR_MAX_USES : F1_PLAYOFF_MAX_USES;

    // First, calculate usage for each driver
    const driverUsageList = drivers.map(driver => {
      const usage = driverUsageHistory[driver.id] || { raceIds: [], count: 0 };
      const usedInRaces = usage.raceIds
        .map(raceId => races.find(r => r.id === raceId))
        .filter(Boolean)
        .map(r => r!.raceNumber);
      return {
        driver,
        usageCount: usage.count,
        maxUses,
        isMaxedOut: usage.count >= maxUses,
        usedInRaces,
      };
    });

    // Group by team
    const grouped: Record<string, typeof driverUsageList> = {};
    for (const item of driverUsageList) {
      const teamName = item.driver.teamName;
      if (!grouped[teamName]) {
        grouped[teamName] = [];
      }
      grouped[teamName].push(item);
    }

    // Sort teams alphabetically and drivers within team by name
    const sortedTeams = Object.keys(grouped).sort();
    return sortedTeams.map(teamName => ({
      teamName,
      drivers: grouped[teamName].sort((a, b) => a.driver.name.localeCompare(b.driver.name)),
    }));
  }, [drivers, driverUsageHistory, phase, races]);

  // Group drivers by team for selection
  const driversByTeam = useMemo(() => {
    const grouped: Record<string, F1Driver[]> = {};
    for (const driver of drivers) {
      if (!grouped[driver.teamName]) {
        grouped[driver.teamName] = [];
      }
      grouped[driver.teamName].push(driver);
    }
    return grouped;
  }, [drivers]);

  // Check if a race is editable
  function isRaceEditable(race: F1Race) {
    const raceDate = new Date(race.scheduledAt);
    const now = new Date();
    return race.status !== 'COMPLETED' && raceDate > now;
  }

  // Get picks for a specific race
  function getRacePicks(raceId: string): F1RacePick | undefined {
    return picks.find(p => p.raceId === raceId);
  }

  // Get selected driver IDs for a race
  function getSelectedDriverIds(raceId: string): string[] {
    const racePick = getRacePicks(raceId);
    return racePick?.drivers.map(d => d.driverId) || [];
  }

  // Check if driver is available for a specific race
  function isDriverAvailable(driverId: string, raceId: string): boolean {
    const maxUses = phase === 'REGULAR' ? F1_REGULAR_MAX_USES : F1_PLAYOFF_MAX_USES;
    const usage = driverUsageHistory[driverId] || { raceIds: [], count: 0 };
    const isInThisRace = usage.raceIds.includes(raceId);
    const effectiveCount = isInThisRace ? usage.count - 1 : usage.count;
    return effectiveCount < maxUses || isInThisRace;
  }

  // Toggle driver selection for a race
  function toggleDriverForRace(driver: F1Driver, race: F1Race) {
    if (!isRaceEditable(race)) return;

    const selectedIds = getSelectedDriverIds(race.id);
    const isSelected = selectedIds.includes(driver.id);

    if (isSelected) {
      removeDriverFromRace(driver.id, race);
    } else {
      if (!isDriverAvailable(driver.id, race.id)) return;
      if (selectedIds.length >= F1_DRIVERS_PER_RACE) return;
      addDriverToRace(driver, race);
    }
  }

  function addDriverToRace(driver: F1Driver, race: F1Race) {
    const newDriver: F1DriverPick = {
      driverId: driver.id,
      driverName: driver.name,
      teamId: driver.teamId,
      teamName: driver.teamName,
    };

    setPicks(prev => {
      const existingPick = prev.find(p => p.raceId === race.id);
      if (existingPick) {
        return prev.map(p =>
          p.raceId === race.id
            ? { ...p, drivers: [...p.drivers, newDriver] }
            : p
        );
      } else {
        return [
          ...prev,
          {
            raceId: race.id,
            raceNumber: race.raceNumber,
            raceName: race.name,
            drivers: [newDriver],
          },
        ];
      }
    });

    setDriverUsageHistory(prev => {
      const usage = prev[driver.id] || { raceIds: [], count: 0 };
      if (!usage.raceIds.includes(race.id)) {
        return {
          ...prev,
          [driver.id]: {
            raceIds: [...usage.raceIds, race.id],
            count: usage.count + 1,
          },
        };
      }
      return prev;
    });
  }

  function removeDriverFromRace(driverId: string, race: F1Race) {
    setPicks(prev =>
      prev.map(p =>
        p.raceId === race.id
          ? { ...p, drivers: p.drivers.filter(d => d.driverId !== driverId) }
          : p
      ).filter(p => p.drivers.length > 0)
    );

    setDriverUsageHistory(prev => {
      const usage = prev[driverId];
      if (usage && usage.raceIds.includes(race.id)) {
        const newRaceIds = usage.raceIds.filter(id => id !== race.id);
        if (newRaceIds.length === 0) {
          const { [driverId]: _, ...rest } = prev;
          return rest;
        }
        return {
          ...prev,
          [driverId]: {
            raceIds: newRaceIds,
            count: newRaceIds.length,
          },
        };
      }
      return prev;
    });
  }

  // Toggle race expansion
  function toggleRaceExpand(raceId: string) {
    setExpandedRaces(prev => {
      const next = new Set(prev);
      if (next.has(raceId)) {
        next.delete(raceId);
      } else {
        next.add(raceId);
      }
      return next;
    });
  }

  // Calculate progress
  const pickedRaces = useMemo(() => {
    return picks.filter(p => p.drivers.length === F1_DRIVERS_PER_RACE).length;
  }, [picks]);

  // Submit entry
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!pool) {
      setError('Pool not loaded');
      return;
    }

    if (pickedRaces < 1) {
      setError('Please make picks for at least one race');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const token = await getAccessTokenSilently();

      const entryPicks: F1EntryPicks = {
        type: 'F1_SEQUENTIAL_RACES',
        season: new Date().getFullYear(),
        phase,
        driverUsageHistory,
        picks: picks.filter(p => p.drivers.length === F1_DRIVERS_PER_RACE),
      };

      const hasEntryFee = pool.entryFee && Number(pool.entryFee) > 0;
      const entryData: any = { entryName, picks: entryPicks };

      // Use actual pool ID for API calls
      if (isEditMode && entryId) {
        await api.updateEntry(pool.id, entryId, entryData, token);
        navigate(`/pools/${pool.slug || pool.id}`);
      } else {
        await api.createEntry(pool.id, entryData, token);
        if (hasEntryFee) {
          // Payment v2: entry is saved — take them to Settle Up to pay
          navigate('/settle-up', {
            state: { justSubmitted: true, submittedPool: pool.name },
          });
        } else {
          navigate(`/pools/${pool.slug || pool.id}`);
        }
      }
    } catch (err) {
      console.error('Failed to submit:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit entry');
    } finally {
      setSubmitting(false);
    }
  }

  // Render a single race card
  function renderRaceCard(race: F1Race) {
    const racePick = getRacePicks(race.id);
    const selectedIds = getSelectedDriverIds(race.id);
    const isComplete = racePick?.drivers.length === F1_DRIVERS_PER_RACE;
    const isLocked = !isRaceEditable(race);
    const isExpanded = expandedRaces.has(race.id);
    const maxUses = phase === 'REGULAR' ? F1_REGULAR_MAX_USES : F1_PLAYOFF_MAX_USES;
    const raceFlag = getRaceFlag(race.name, race.location);

    return (
      <div
        key={race.id}
        className={`border rounded-lg overflow-hidden ${
          isComplete ? 'border-green-300 bg-green-50' :
          isLocked ? 'border-gray-200 bg-gray-50' :
          'border-gray-200 bg-white'
        }`}
      >
        {/* Race Header - Always visible */}
        <button
          type="button"
          onClick={() => toggleRaceExpand(race.id)}
          className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <span className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold ${
              isComplete ? 'bg-green-500 text-white' :
              isLocked ? 'bg-gray-400 text-white' :
              'bg-red-600 text-white'
            }`}>
              {race.raceNumber}
            </span>
            <div>
              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                <span className="text-lg">{raceFlag}</span>
                {race.name}
              </h4>
              <p className="text-sm text-gray-500">
                {race.location} • {new Date(race.scheduledAt).toLocaleDateString()}
                {race.isSprintRace && (
                  <span className="ml-2 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">
                    ⚡ Sprint
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Selected drivers preview with team colors */}
            <div className="flex gap-1">
              {[0, 1].map(slot => {
                const driver = racePick?.drivers[slot];
                const teamColors = driver?.teamName ? getTeamColors(driver.teamName) : null;
                return (
                  <span
                    key={slot}
                    className="px-2 py-1 text-xs rounded font-bold"
                    style={driver && teamColors ? {
                      backgroundColor: teamColors.primary,
                      color: '#FFFFFF',
                    } : {
                      backgroundColor: '#E5E7EB',
                      color: '#9CA3AF',
                    }}
                  >
                    {driver ? driver.driverName.split(' ').pop() : 'Empty'}
                  </span>
                );
              })}
            </div>
            {isLocked && <span className="text-gray-400">🔒</span>}
            {isComplete && !isLocked && <span className="text-green-500">✓</span>}
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>

        {/* Expanded Content - Driver Selection */}
        {isExpanded && (
          <div className="border-t border-gray-200 p-4">
            {isLocked ? (
              <p className="text-sm text-gray-500 italic">This race is locked. Picks cannot be changed.</p>
            ) : (
              <>
                {/* Selected Drivers with team colors */}
                <div className="mb-4">
                  <h5 className="text-sm font-medium text-gray-700 mb-2">
                    Selected Drivers ({selectedIds.length}/{F1_DRIVERS_PER_RACE})
                  </h5>
                  <div className="grid grid-cols-2 gap-3">
                    {[0, 1].map(slot => {
                      const driverPick = racePick?.drivers[slot];
                      const driverData = driverPick ? drivers.find(d => d.id === driverPick.driverId) : null;
                      const teamColors = driverData ? getTeamColors(driverData.teamName) : null;
                      return (
                        <div
                          key={slot}
                          className="p-3 rounded-lg border-2 transition-all"
                          style={driverPick && teamColors ? {
                            backgroundColor: teamColors.primary,
                            borderColor: teamColors.secondary,
                          } : {
                            borderColor: '#E5E7EB',
                            borderStyle: 'dashed',
                            backgroundColor: '#F9FAFB',
                          }}
                        >
                          {driverPick && driverData ? (
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <span
                                    className="w-1.5 h-8 rounded-full"
                                    style={{ backgroundColor: teamColors?.secondary }}
                                  />
                                  <div>
                                    <div className="font-bold text-white">{driverPick.driverName}</div>
                                    <div className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.75)' }}>
                                      {driverData.teamName}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeDriverFromRace(driverPick.driverId, race)}
                                className="w-6 h-6 rounded-full bg-white/20 text-white hover:bg-white/30 flex items-center justify-center text-lg leading-none font-bold"
                              >
                                ×
                              </button>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400 block text-center py-2">Select driver {slot + 1}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Available Drivers by Team with team colors */}
                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-3">Available Drivers</h5>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {Object.entries(driversByTeam).map(([teamName, teamDrivers]) => {
                      const teamColors = getTeamColors(teamName);
                      return teamDrivers.map(driver => {
                        const isSelected = selectedIds.includes(driver.id);
                        const isAvailable = isDriverAvailable(driver.id, race.id);
                        const usage = driverUsageHistory[driver.id]?.count || 0;
                        const canSelect = isAvailable && (isSelected || selectedIds.length < F1_DRIVERS_PER_RACE);
                        const isMaxed = usage >= maxUses && !isSelected;

                        return (
                          <button
                            key={driver.id}
                            type="button"
                            onClick={() => toggleDriverForRace(driver, race)}
                            disabled={!canSelect && !isSelected}
                            className={`p-2 rounded-lg text-left text-sm transition-all relative overflow-hidden border-2 ${
                              isSelected
                                ? 'ring-2 ring-offset-1 ring-gray-900'
                                : canSelect
                                  ? 'hover:shadow-lg hover:scale-[1.02]'
                                  : 'opacity-50 cursor-not-allowed'
                            }`}
                            style={{
                              backgroundColor: isMaxed ? '#E5E7EB' : teamColors.primary,
                              borderColor: isSelected ? '#1F2937' : teamColors.secondary,
                            }}
                          >
                            <div className="flex items-center justify-between gap-1">
                              <div className="min-w-0 flex-1">
                                <div className="font-bold truncate" style={{ color: '#FFFFFF' }}>{driver.name}</div>
                                <div className="text-xs font-medium truncate" style={{ color: 'rgba(255,255,255,0.75)' }}>
                                  {driver.teamName}
                                </div>
                              </div>
                              <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 font-bold ${
                                isMaxed
                                  ? 'bg-gray-400 text-white'
                                  : usage > 0
                                    ? 'bg-yellow-500 text-white'
                                    : 'bg-green-500 text-white'
                              }`}>
                                {usage}/{maxUses}
                              </span>
                            </div>
                          </button>
                        );
                      });
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !pool) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
        <Link to="/pools" className="mt-4 inline-block text-red-600 hover:text-red-800">
          ← Back to Pools
        </Link>
      </div>
    );
  }

  const maxUses = phase === 'REGULAR' ? F1_REGULAR_MAX_USES : F1_PLAYOFF_MAX_USES;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <Link to={`/pools/${poolId}`} className="text-red-600 hover:text-red-800 mb-2 inline-block">
          ← Back to {pool?.name}
        </Link>
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditMode ? 'Edit Your Entry' : 'Create Your Entry'}
        </h1>
        <p className="text-gray-600 mt-1">
          Select 2 drivers for each race. Each driver can be used up to {maxUses} time{maxUses > 1 ? 's' : ''} in the {phase.toLowerCase()} season.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-6">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Race List */}
          <div className="lg:col-span-2 space-y-6">
            {/* Entry Name */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <label htmlFor="entryName" className="block text-sm font-medium text-gray-700 mb-1">
                Entry Name (optional)
              </label>
              <input
                type="text"
                id="entryName"
                value={entryName}
                onChange={e => setEntryName(e.target.value)}
                placeholder="My F1 Entry"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            {/* Progress */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Races Complete: {pickedRaces} / {races.length}
                </span>
                <span className="text-sm text-gray-500">
                  {races.length > 0 ? Math.round((pickedRaces / races.length) * 100) : 0}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-red-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${races.length > 0 ? (pickedRaces / races.length) * 100 : 0}%` }}
                />
              </div>
            </div>

            {/* Regular Season Races */}
            {regularSeasonRaces.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowRegularSeason(!showRegularSeason)}
                  className="w-full p-4 flex items-center justify-between bg-blue-50 hover:bg-blue-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-blue-600 text-white text-sm font-medium rounded-full">
                      Regular Season
                    </span>
                    <span className="text-sm text-gray-600">
                      Races 1-{F1_REGULAR_SEASON_RACES} • Max {F1_REGULAR_MAX_USES} uses per driver
                    </span>
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-500 transition-transform ${showRegularSeason ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showRegularSeason && (
                  <div className="p-4 space-y-3">
                    {regularSeasonRaces.map(race => renderRaceCard(race))}
                  </div>
                )}
              </div>
            )}

            {/* Playoff Races */}
            {playoffRaces.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowPlayoffs(!showPlayoffs)}
                  className="w-full p-4 flex items-center justify-between bg-purple-50 hover:bg-purple-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-purple-600 text-white text-sm font-medium rounded-full">
                      Playoffs
                    </span>
                    <span className="text-sm text-gray-600">
                      Races {F1_REGULAR_SEASON_RACES + 1}-24 • Max {F1_PLAYOFF_MAX_USES} use per driver
                    </span>
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-500 transition-transform ${showPlayoffs ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showPlayoffs && (
                  <div className="p-4 space-y-3">
                    {playoffRaces.map(race => renderRaceCard(race))}
                  </div>
                )}
              </div>
            )}

            {/* No races message */}
            {races.length === 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                <p className="text-yellow-800">No races have been scheduled for this pool yet.</p>
                <p className="text-sm text-yellow-600 mt-1">Check back later or contact the pool administrator.</p>
              </div>
            )}
          </div>

          {/* Right Column - Driver Usage & Submit */}
          <div className="lg:sticky lg:top-4 space-y-4 self-start">
            {/* Driver Usage Summary */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setShowDriverUsage(!showDriverUsage)}
                className="w-full p-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">🏎️ Driver Usage</h3>
                <svg
                  className={`w-5 h-5 text-gray-500 transition-transform ${showDriverUsage ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showDriverUsage && (
                <div className="p-4 max-h-[calc(100vh-350px)] overflow-y-auto">
                  {driverUsageByTeam.length === 0 ? (
                    <p className="text-sm text-gray-500">No drivers available</p>
                  ) : (
                    <div className="space-y-4">
                      {driverUsageByTeam.map(({ teamName, drivers: teamDrivers }) => {
                        const teamColors = getTeamColors(teamName);
                        return (
                          <div key={teamName} className="rounded-lg overflow-hidden shadow-sm">
                            {/* Team Header - darker shade */}
                            <div
                              className="flex items-center justify-between px-3 py-2"
                              style={{
                                backgroundColor: teamColors.primary,
                                boxShadow: 'inset 0 0 0 100px rgba(0,0,0,0.25)',
                              }}
                            >
                              <span className="font-black text-sm uppercase tracking-wide text-white">
                                {teamName}
                              </span>
                            </div>
                            {/* Team Drivers - lighter rows */}
                            <div>
                              {teamDrivers.map(({ driver, usageCount, maxUses: driverMaxUses, isMaxedOut, usedInRaces }) => (
                                <div
                                  key={driver.id}
                                  className="px-3 py-1.5 transition-all"
                                  style={{
                                    backgroundColor: isMaxedOut ? '#E5E7EB' : teamColors.primary,
                                  }}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="min-w-0 flex-1">
                                      <div
                                        className="text-sm font-medium"
                                        style={{ color: isMaxedOut ? '#6B7280' : '#FFFFFF' }}
                                      >
                                        {driver.name}
                                      </div>
                                    </div>
                                    <div className="text-right flex-shrink-0 ml-2">
                                      <div className="flex items-center gap-1">
                                        {/* Usage bar visualization */}
                                        <div className="flex gap-0.5">
                                          {Array.from({ length: driverMaxUses }).map((_, i) => (
                                            <div
                                              key={i}
                                              className="w-3 h-3 rounded-sm"
                                              style={{
                                                backgroundColor: i < usageCount ? '#FFFFFF' : 'rgba(255,255,255,0.3)',
                                              }}
                                            />
                                          ))}
                                        </div>
                                        <span className={`text-xs font-bold ml-1 ${
                                          isMaxedOut ? 'text-gray-500' : 'text-white'
                                        }`}>
                                          {usageCount}/{driverMaxUses}
                                        </span>
                                      </div>
                                      {usedInRaces.length > 0 && (
                                        <div className="text-xs mt-0.5" style={{ color: isMaxedOut ? '#9CA3AF' : 'rgba(255,255,255,0.7)' }}>
                                          R{usedInRaces.join(', R')}
                                        </div>
                                      )}
                                      {isMaxedOut && (
                                        <span className="text-xs text-gray-500 font-medium">🔒</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Entry fee notice — payment happens after submit (Settle Up) */}
            {pool && pool.entryFee && Number(pool.entryFee) > 0 && !isEditMode && (
              <FeeNotice entryFee={Number(pool.entryFee)} />
            )}

            {/* Submit Button */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <button
                type="submit"
                disabled={submitting || pickedRaces < 1}
                className="w-full px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Saving...' : isEditMode ? 'Update Entry' : 'Submit Entry'}
              </button>
              <p className="text-xs text-gray-500 text-center mt-2">
                {pickedRaces} race{pickedRaces !== 1 ? 's' : ''} with picks selected
              </p>
            </div>

            {/* Cancel Link */}
            <Link
              to={`/pools/${poolId}`}
              className="block text-center text-gray-600 hover:text-gray-900"
            >
              Cancel
            </Link>
          </div>
        </div>
      </form>
    </div>
  );
}
